<template>
  <v-template>
    <Loader
      v-show="loading"
      :loading="loading"
    />
    <v-card>
      <v-card-title>
        <v-row no-gutters>
          <span class="headline font-weight-medium">{{ header }}</span>
          <v-spacer />
          <v-dialog
            v-model="logWorkDialog"
            persistent
            max-width="600px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="primary"
                dark
                v-bind="attrs"
                v-on="on"
              >
                Журнал робіт
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">Журнал робіт</span>
                <v-spacer />
                <IconBtn
                  icons="close"
                  :large="true"
                  color="grey darken-4"
                  @click="logWorkDialog = false"
                />
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12">
                    <CustomTextField
                      v-model="logTime"
                      label="Витрачений час"
                      :hide-details="true"
                      :required="true"
                      :error-messages="$v.logTime.$invalid ? errors.LOG_TIME : []"
                      @input="isChanged = true"
                    />
                    <div class="pl-2">
                      (Наприклад, 3d 4h 12m)
                    </div>
                  </v-col>
                  <v-col cols="12">
                    <v-textarea
                      v-model="description"
                      outlined
                      label="Опис"
                      :hide-details="true"
                      @input="isChanged = true"
                    />
                  </v-col>
                </v-row>
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <PrimaryBtn
                  :loading="loading"
                  :disabled="$v.logTime.$invalid"
                  @click="saveLog"
                />
              </v-card-actions>
            </v-card>
          </v-dialog>
          <IconBtn
            icons="close"
            :large="true"
            color="grey darken-4"
            @click="close"
          />
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <CustomTextField
              v-model="title"
              label="Найменування"
              :hide-details="true"
              :required="true"
              :error-messages="$v.title.$invalid ? errors.TITLE : []"
              @input="isChanged = true"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <ProjectSelect
              v-model="project"
              :error-messages="$v.project.$invalid ? errors.PROJECT : []"
              class="pr-1"
              :required="true"
              @input="isChanged = true"
            />
          </v-col>
          <v-col>
            <UserSelect
              v-model="implementer"
              :multiple-select="false"
              label="Виконавець*"
              placeholder="Введіть iм’я виконавця"
              :error-messages="$v.implementer.$invalid ? errors.IMPLEMENTER : []"
              :required="true"
              @input="isChanged = true"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <EnumSelect
              v-model="issueCurrentStatus"
              :items="issueStatuses"
              label="Статус"
              placeholder="Виберіть статус задачі"
              clearable
              :hide-details="true"
            />
          </v-col>
          <v-col>
            <DatePicker
              v-model="planDate"
              default-value
              :filled="false"
              label="Дата"
              :hide-details="true"
              @input="isChanged = true"
            />
          </v-col>
        </v-row>
        <Markdown
          :id="id"
          v-model="content"
          @input="isChanged = true"
        />
      </v-card-text>
      <v-card-actions>
        <PrimaryBtn
          :loading="loading"
          :disabled="!isChanged || isFormInvalid"
          @click="save"
        />
      </v-card-actions>
    </v-card>
  </v-template>
</template>

<script>
import * as issueApi from "../../backend/issuesApi.js";
import * as logWorksApi from "../../backend/logWorksApi.js";
import Loader from "../../../components/Loader.vue";
import IconBtn from "../../../components/IconBtn.vue";
import Markdown from "../../components/Markdown.vue";
import PrimaryBtn from "../../../components/PrimaryBtn.vue";
import CustomTextField from "../../../components/CustomTextField.vue";
import { ERRORS } from "@/helpers";
import UserSelect from "../../../views/system/users/UserSelect.vue";
import ProjectSelect from "../projects/ProjectSelect.vue";
import EnumSelect from "@/components/selects/EnumSelect";
import IssueStatuses from "../../helpers/issueStatuses.js";
import DatePicker from "@/components/story/DatePicker";
import { required, helpers } from "vuelidate/lib/validators";

const chekLogs = helpers.regex("chekLogs", /^((\d{1,2}[d]{1}\s{0,1})(\d{1,2}[h]{1}\s{0,1})(\d{1,2}[m]{1}\s{0,1})){1}$|^((\d{1,2}[d]{1}\s{0,1})(\d{1,2}[h]{1}\s{0,1})){1}$|^((\d{1,2}[h]{1}\s{0,1})(\d{1,2}[m]{1}\s{0,1})){1}$|^((\d{1,2}[d]{1}\s{0,1})|(\d{1,2}[h]{1}\s{0,1})|(\d{1,2}[m]{1}\s{0,1})){1}$/g);

export default {
  components: {
    Loader,
    IconBtn,
    Markdown,
    PrimaryBtn,
    CustomTextField,
    UserSelect,
    ProjectSelect,
    EnumSelect,
    DatePicker,
  },
  props: ["id"],
  data: () => ({
    loading: false,
    isChanged: false,
    content: "",
    title: "",
    logTime: "",
    description: "",
    logWorkDialog: false,
    project: null,
    implementer: null,
    creator: null,
    issueCurrentStatus: 1,
    planDate: "",
  }),
  validations: {
    title: {
      required,
    },
    project: {
      required,
    },
    implementer: {
      required,
    },
    logTime: {
      required,
      chekLogs,
    },
  },
  computed: {
    isFormInvalid(){
      return this.$v.title.$invalid || this.$v.project.$invalid || this.$v.implementer.$invalid;
    },
    isNew() {
      return this.id != 0 ? false : true;
    },
    header() {
      return this.isNew ? "Створення завдання" : "Редагування завдання";
    },
    errors() {
      return {
        TITLE: [ERRORS.REQUIRED],
        DATE: [ERRORS.REQUIRED],
        PROJECT: [ERRORS.REQUIRED],
        IMPLEMENTER: [ERRORS.REQUIRED],
        LOG_TIME: [ERRORS.LOG_TIME],
      };
    },
    issueStatuses(){
      return [
        {
          value: IssueStatuses.TO_DO.value,
          text: IssueStatuses.TO_DO.title,
        },
        {
          value: IssueStatuses.DISPUTE.value,
          text: IssueStatuses.DISPUTE.title,
        },
        {
          value: IssueStatuses.IN_PROGRESS.value,
          text: IssueStatuses.IN_PROGRESS.title,
        },
        {
          value: IssueStatuses.DONE.value,
          text: IssueStatuses.DONE.title,
        },
      ];
    },
  },
  async created() {
    if (this.id != 0) {
      await this.getIssue();
    }
  },
  methods: {
    async getIssue() {
      await issueApi.getIssue(this.id).then((issue) => {
        this.title = issue.title;
        this.content = issue.description;
        this.planDate = issue.planDate;
        this.issueCurrentStatus = issue.status;

        if (issue.implementer !== null && issue.implementer !== undefined) {
          this.implementer = issue.implementer;
        }
        if (issue.project !== null && issue.project !== undefined) {
          this.project = issue.project;
        }
      });
    },
    async save() {
      this.loading = true;
      if (this.id == 0) {
        await issueApi
          .createIssue({
            title: this.title,
            description: this.content,
            projectId: this.project.id,
            implementerId: this.implementer.id,
            planDate: this.planDate,
            status: this.issueCurrentStatus,
          })
          .then(() => {
            this.$router.push({ path: "/projects-issues" });
          });
      } else {
        await issueApi.updateIssue(this.id, {
          title: this.title,
          description: this.content,
          projectId: this.project.id,
          implementerId: this.implementer.id,
          planDate: this.planDate,
          status: this.issueCurrentStatus,
        });
        this.$notifySuccess();
      }
      this.loading = false;
    },
    close() {
      if (this.modal) {
        this.$emit("close");
      } else {
        this.$router.go(-1);
      }
    },
    async saveLog() {
      this.logWorkDialog = false;
      await logWorksApi.createLog({
        time: this.logTime,
        description: this.description,
        issueId: this.id,
      });
      this.logTime = "";
    },
  },
};
</script>